import React from 'react';

import RichNotesEditor from './RichNotesEditor';

/**
 * @typedef {Object} Props
 * @property {React$Node} [children] - Rendered at the top of the body, e.g.
 * the pane's share controls, so they share the editor's card and padding.
 * @property {Function} onChange - (text, richText) change callback.
 * @property {Function} onRetry - Save/load retry callback.
 * @property {string} [privacyLabel] - Share status text for the chip.
 * @property {Object|null} [richText] - Quill document, if any.
 * @property {string|null} [roomName] - Room owning the notes.
 * @property {string} saveStatus - 'saved'|'saving'|'error'|'loadError'.
 * @property {boolean} [showPrivacyChip] - Hide the chip in the pane (the share
 * header carries the status there); the popup keeps it.
 * @property {Function} t - Translation function.
 * @property {string} text - Plain notes text.
 */

/**
 * Shared editor and save feedback for the meeting pane and attached window.
 * The pane passes {@code showPrivacyChip: false} because its share panel
 * already carries the live share status; the popup keeps the chip.
 *
 * @param {Props} props - Notes document and callbacks.
 * @returns {React$Element}
 */
export default function NotesContent({ children, text, richText, roomName, saveStatus, onChange, onRetry, privacyLabel, showPrivacyChip = true, t }) {
    const status = (
        <div
            className = { `notes_pane-save notes_pane-save--${saveStatus}` }
            role = 'status'>
            <span className = 'notes_pane-save-dot' />
            <span>{t(`notesPane.${{ saved: 'savedLocally',
                saving: 'saving',
                error: 'saveFailed',
                loadError: 'loadFailed' }[saveStatus]}`)}</span>
            {(saveStatus === 'error' || saveStatus === 'loadError') && (
                <button
                    onClick = { onRetry }
                    type = 'button'>{t('notesPane.retry')}</button>
            )}
        </div>
    );

    return (
        <div className = 'notes_pane-body'>
            <div className = 'notes_pane-intro'>
                {children}
                {showPrivacyChip && <span className = 'notes_pane-private'>{privacyLabel || t('notesPane.private')}</span>}
            </div>
            {saveStatus === 'loadError' ? status : (
                <RichNotesEditor
                    key = { roomName }
                    onChange = { onChange }
                    richText = { richText }
                    roomName = { roomName }
                    t = { t }
                    text = { text }>
                    {status}
                </RichNotesEditor>
            )}
        </div>
    );
}
